import * as React from "react";

// MUI
import {
	withStyles,
	WithStyles,
	createStyles,
	Theme,
} from "@material-ui/core/styles";
import {
	Accordion,
	GridProps,
	AccordionSummary,
	AccordionDetails,
	Grid,
	Divider,
} from "@material-ui/core";

// NPM
import clsx from "clsx";
import { animated } from "react-spring";

// Classes
import Utility from "../../domain/classes/Utility";
import Doctor from "../../domain/classes/Doctor";
import AvailableSlot from "../../domain/classes/AvailableSlot";

// Components
import StyledDoctorSummaryHeader from "./DoctorSummaryHeader.Component";

// Interfaces
import IAppointmentRecord from "../../domain/interfaces/IAppointmentRecord";
import IAvailableSlot from "../../domain/interfaces/IAvailableSlot";
import IAppointmentTypeProviderDto from "../../domain/interfaces/IAppointmentTypeProviderDto";

// Contexts
import { useApplicationManager } from "../../contexts/ApplicationManager.Context";

export const styles = (theme: Theme) => createStyles({ root: {} });

export interface AppointmentsAccordionProps extends GridProps {
	data: IAppointmentRecord;
}

const AppointmentsAccordion = React.forwardRef<
	HTMLDivElement,
	AppointmentsAccordionProps & WithStyles<typeof styles>
>(({ children, className, classes, data }, ref) => {
	const { apptTypeOverrides, selectedApptType } = useApplicationManager();

	return (
		<Grid
			item
			xs={12}
			className={clsx(classes.root, className)}
			ref={ref}
			component="div"
		>
			<Accordion square>
				<AccordionSummary component="div">
					<StyledDoctorSummaryHeader
						data={data}
						slots={data.availableSlots.filter(
							(
								slot: IAvailableSlot,
								index: number,
								array: IAvailableSlot[]
							) => {
								const overrideData = Doctor.getProviderOverrides(
									apptTypeOverrides,
									data.userId
								);

								let minDuration = 0;

								if (!overrideData) {
									minDuration = 0;
								}

								minDuration = !!!(overrideData as IAppointmentTypeProviderDto)
									.minimumDurationOverride
									? selectedApptType.minimumDuration
									: (overrideData as IAppointmentTypeProviderDto)
											.minimumDurationOverride;

								return AvailableSlot.filterSlotDuration(
									slot,
									index,
									array,
									minDuration
								);
							}
						)}
					/>
				</AccordionSummary>
				<Divider />
				<AccordionDetails>
					<Grid container spacing={1}>
						{children}
					</Grid>
				</AccordionDetails>
			</Accordion>
		</Grid>
	);
});

const StyledAppointmentsAccordion = withStyles(styles, {
	classNamePrefix: Utility.generateClassName("AppointmentsAccordion"),
})(AppointmentsAccordion);

export default StyledAppointmentsAccordion;

export const AnimatedAppointmentsAccordion = animated(
	StyledAppointmentsAccordion
);
